import React from 'react';
import VisaPageLayout2, { FaqItem, ProcessStep, RequirementItem, VisaCategory, VisaPlan } from './visa-page-layout-2';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const categories: VisaCategory[] = [
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" /><path d="M12 6v6l4 2" /></svg>),
    title: 'eTA — Visa-Exempt Travelers',
    text: 'A quick electronic authorization for eligible passport holders flying into Canada.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z" /><circle cx="12" cy="12" r="3" /></svg>),
    title: 'Visitor Visa (TRV)',
    text: 'For tourism, family visits, or business, required for nationalities not eligible for the eTA.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>),
    title: 'Super Visa',
    text: 'A multi-year visitor visa for parents and grandparents of Canadian citizens or permanent residents.',
  },
];

const plans: VisaPlan[] = [
  { id: 'ca-eta', name: 'Electronic Travel Authorization (eTA)', entryType: 'Multiple Entry', validity: 'Valid for 5 years or until passport expiry', stay: 'Up to 6 months per visit', processing: 'Minutes to 3 business days', price: '15', currency: 'OMR' },
  { id: 'ca-trv', name: 'Temporary Resident Visa (TRV)', entryType: 'Single or Multiple Entry', validity: 'Up to 10 years, nationality-dependent', stay: 'Up to 6 months per visit', processing: '3–6 weeks', price: '85', currency: 'OMR', featured: true },
  { id: 'ca-super', name: 'Super Visa — Parents & Grandparents', entryType: 'Multiple Entry', validity: 'Valid for up to 10 years', stay: 'Up to 5 years per visit', processing: '6–10 weeks', price: '160', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport', detail: 'Valid for the full duration of your intended stay in Canada.' },
  { label: 'Digital photo', detail: 'Recent photo meeting IRCC specifications, uploaded with your online application.' },
  { label: 'Proof of funds', detail: 'Bank statements showing you can support yourself (and any dependents) during your visit.' },
  { label: 'Letter of invitation (if applicable)', detail: 'From a host in Canada, including their status and contact details, for family or friend visits.' },
  { label: 'Ties to home country', detail: 'Evidence of employment, property, or family showing your intent to return home.' },
  { label: 'Travel history', detail: 'Previous visas and travel stamps, which support a stronger overall application.' },
  { label: 'Medical insurance (Super Visa only)', detail: 'Minimum one year of Canadian medical insurance coverage, required specifically for Super Visa applicants.' },
];

const goodToKnow: string[] = [
  'Whether you need an eTA or a full Visitor Visa depends entirely on your passport nationality — we\'ll confirm which route applies to you before anything else.',
  'The eTA is electronically linked to your passport, so a new passport means applying again.',
  'The Super Visa allows much longer stays than a standard visitor visa, but requires proof of a Canadian host\'s income and dedicated medical insurance.',
  'Processing times can shift with application volume — we track current IRCC timelines when planning your submission date.',
];

const steps: ProcessStep[] = [
  { title: 'Eligibility & category review', text: 'We confirm whether you need an eTA, a Visitor Visa, or a Super Visa based on your nationality and purpose.' },
  { title: 'Document preparation', text: 'Our team compiles your financial, travel, and supporting documents to match IRCC requirements.' },
  { title: 'Application submission', text: 'We submit your application through the appropriate online IRCC portal.' },
  { title: 'Biometrics (if required)', text: 'Some applicants need to give fingerprints and a photo at a local visa application centre — we\'ll book this for you.' },
  { title: 'Decision & passport request', text: 'We monitor your application status and guide you through passport submission once approved.' },
];

const faqs: FaqItem[] = [
  { question: 'How do I know if I need an eTA or a full visitor visa?', answer: 'It\'s determined entirely by your passport\'s nationality. Send us your passport details and travel dates, and we\'ll confirm the correct route immediately.' },
  { question: 'Is the eTA really as fast as it sounds?', answer: 'Most eTA applications are approved within minutes, though some are flagged for manual review and can take several days — we\'ll let you know if that happens.' },
  { question: 'What makes the Super Visa different from a regular visitor visa?', answer: 'The Super Visa allows parents and grandparents of Canadian citizens or permanent residents to stay far longer per visit, but it requires a formal invitation, proof of the host\'s income, and dedicated medical insurance.' },
  { question: 'Can biometrics be given locally, or do I need to travel?', answer: 'Biometrics are typically collected at a local Visa Application Centre — we\'ll confirm the nearest location and book your appointment as part of the process.' },
  { question: 'What if my visitor visa application is refused?', answer: 'We review the refusal letter with you to understand the stated reason, and advise honestly on whether a stronger follow-up application is worth pursuing.' },
];

export default function VisaCanadaPage() {
   const { plans, loading, error } = useVisaPacksByCountry('CAN');
  return (
    <VisaPageLayout2
      countryName="Canada"
      flagEmoji="🇨🇦"
      heroEyebrow="Canada"
      heroImage="https://images.unsplash.com/photo-1749098140326-3dc9cc3b7d97?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Canada"
      heroTitleTail="visa, matched to the right category"
      heroSubtitle="From Toronto's skyline to the Rockies — Canada's entry system has more than one route in. We confirm exactly which one applies to you, then handle it end to end."
      trustStats={[
        { value: '3–10', label: 'Weeks (TRV)' },
        { value: '92%', label: 'Approval Rate' },
        { value: '1,650+', label: 'Canada Visas Issued' },
      ]}
      categories={categories}
      plans={plans}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      useTimeline
      faqs={faqs}
      ctaSubtitle="Tell us your passport nationality and travel plans — we'll confirm whether you need an eTA, a Visitor Visa, or a Super Visa before you spend a cent."
    />
  );
}